import { Editor } from '@tiptap/react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileManager } from 'src/editor/shared/ui-lib/uploader/file-manager'
import { mimeTypes } from 'client/constants/editorSettings'
import { LinkAttributes } from '../../enums/link-attributes-enum'
import { LinkSourceTypeEnum } from '../../enums/link-source-type-enum'
import { useToolbarCommands } from '../../hooks/use-toolbar-commands'
import { LinkType } from '../../type/link-type'
import { detectLinkSource } from '../../utils/detect-link-source'
import { LinkSourceBlog } from './link-source-blog'
import { LinkSourceInput } from './link-source-input'
import { LinkSourcePopup } from './link-source-popup'
import { Button } from 'src/shared/components/button'
import { RadioGroup } from 'src/shared/components/radio-group'
import { twJoin } from 'tailwind-merge'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'

export const NO_FOLLOW_CHECKBOX_ID = 'no-follow-checkbox'
export const OPEN_IN_NEW_TAB_CHECKBOX_ID = 'open-in-new-tab-checkbox'

interface LinkToolbarProps {
  editor: Editor
  close: () => void
  isBlogPage: boolean
}

export const LinkToolbar = ({
  editor,
  close,
  isBlogPage,
}: LinkToolbarProps) => {
  const { onSetLink, onUnsetLink } = useToolbarCommands(editor)
  const { t } = useTranslation()
  const [linkState, setLinkState] = useState<LinkType>(
    editor.getAttributes('link'),
  )
  const [showFileManager, setShowFileManager] = useState(false)
  const [linkSource, setLinkSource] = useState(detectLinkSource(linkState))

  const updateURL = (url: string) => {
    setLinkState({
      rel: linkState.rel,
      target: linkState.target,
      href: url,
    })
  }

  const updatePopupId = (popupid: string) => {
    setLinkState({ popupid })
  }

  const save = () => {
    const isHasLink =
      linkSource === LinkSourceTypeEnum.Popup
        ? !!linkState.popupid
        : !!linkState.href
    if (isHasLink) {
      onSetLink(linkState)
    } else {
      onUnsetLink()
    }
    close()
  }
  const baseOptions = [
    {
      label: t('components.core.text_editor.link_toolbar.link'),
      value: LinkSourceTypeEnum.Input,
    },
    {
      label: t('components.core.text_editor.link_toolbar.popup'),
      value: LinkSourceTypeEnum.Popup,
    },
  ]

  const options = useMemo(() => {
    if (!isBlogPage) {
      return baseOptions
    }
    return [
      ...baseOptions,
      {
        value: LinkSourceTypeEnum.Blog,
        label: t('components.core.text_editor.link_toolbar.blog_link'),
      },
    ]
  }, [isBlogPage])

  const isLinkReadyForSave = useMemo(() => {
    const linkAttrs = editor.getAttributes('link') || {}
    const stateAttrs = linkState || {}

    const currentHref = linkAttrs.href || ''
    const currentPopupId = linkAttrs.popupid || ''
    const stateHref = stateAttrs.href || ''
    const statePopupId = stateAttrs.popupid || ''

    const isPopup = linkSource === LinkSourceTypeEnum.Popup
    return (
      (isPopup ? currentPopupId : currentHref) !==
      (isPopup ? statePopupId : stateHref)
    )
  }, [editor, linkState, linkSource])

  const toggleMakeOpenInNew = () => {
    const newLink: LinkType = {
      ...linkState,
      target:
        linkState.target === LinkAttributes.targetBlank
          ? ''
          : LinkAttributes.targetBlank,
    }
    onSetLink(newLink)
    setLinkState(newLink)
  }

  const toggleNoFollow = () => {
    const newLink: LinkType = {
      ...linkState,
      rel:
        linkState.rel === LinkAttributes.relNoFollow
          ? ''
          : LinkAttributes.relNoFollow,
    }
    onSetLink(newLink)
    setLinkState(newLink)
  }
  useEffect(() => {
    const changeLinkState = () => {
      setLinkState(editor.getAttributes('link'))
      setLinkSource(detectLinkSource(editor.getAttributes('link')))
    }
    editor.on('selectionUpdate', changeLinkState)
    return () => {
      editor.off('selectionUpdate', changeLinkState)
    }
  }, [])

  const remove = () => {
    onUnsetLink()
    close()
  }

  const toggleShowFileManager = () => {
    setShowFileManager(!showFileManager)
  }

  const afterUpload = ({ path }: { path: string }) => {
    setLinkState({
      href: path,
    })
  }
  return (
    <div
      className={twJoin(
        'w-full min-w-[320px] max-w-[450px] rounded-lg bg-white py-3 font-base text-sm not-italic leading-7',
        'my-3.5 box-border shadow-[0px_10px_40px_10px_rgba(0,0,0,0.08)] shadow-custom-heavy',
      )}
    >
      <RadioGroup
        options={options}
        value={linkSource}
        onChange={setLinkSource}
        className="px-6"
      />

      {linkSource === LinkSourceTypeEnum.Input && (
        <LinkSourceInput
          toggleShowFileManager={toggleShowFileManager}
          url={linkState?.href}
          update={updateURL}
          remove={remove}
        />
      )}
      {linkSource === LinkSourceTypeEnum.Popup && (
        <LinkSourcePopup popupid={linkState.popupid} update={updatePopupId} />
      )}
      {linkSource === LinkSourceTypeEnum.Blog && (
        <LinkSourceBlog url={linkState?.href} update={updateURL} />
      )}
      {linkSource !== LinkSourceTypeEnum.Popup && (
        <>
          <div className="my-3 border-gray-200 border-t" />
          <div className="flex flex-col gap-2 px-6">
            <CheckboxNew
              id={OPEN_IN_NEW_TAB_CHECKBOX_ID}
              checked={linkState.target === LinkAttributes.targetBlank}
              onChange={toggleMakeOpenInNew}
              label={
                'components.core.text_editor.link_toolbar.open_link_in_new_tab'
              }
            />
            <CheckboxNew
              id={NO_FOLLOW_CHECKBOX_ID}
              checked={linkState.rel === LinkAttributes.relNoFollow}
              onChange={toggleNoFollow}
              label={'components.core.text_editor.link_toolbar.nofollow'}
            />
          </div>
          <div className="my-3 border-gray-200 border-t" />
        </>
      )}
      <div className="flex justify-end gap-2.5 space-x-2 px-6 pt-2.5 ">
        <Button variant="secondary" onClick={close}>
          {t('components.core.text_editor.link_toolbar.cancel')}
        </Button>
        <Button onClick={save}>
          {t('components.core.text_editor.link_menu.confirm')}
        </Button>
      </div>
      {showFileManager && (
        <FileManager
          closeFileManager={toggleShowFileManager}
          mimeTypes={mimeTypes.all}
          onInsert={afterUpload}
        />
      )}
    </div>
  )
}
